<template>
  <div>
    <ValidationObserver v-slot="{ invalid }">
      <div class="row">
        <!-- SETTINGS CARD SECTION -->
        <div class="col-12 col-xl-3">
          <document-settings :title="$t('message.invoiceDetails')" form>
            <b-form slot="body">
              <ValidationProvider rules="required" v-slot="{ errors }">
                <fg-input
                  :label="$t('message.invoiceRecipient')"
                  :error="errors[0]"
                >
                  <b-form-select
                    v-model="invoice.to"
                    :options="customersOptions"
                  >
                  </b-form-select>
                </fg-input>
              </ValidationProvider>
              <fg-input :label="$t('message.dateSent')">
                <vue-ctk-date-time-picker
                  v-model="invoice.sentDate"
                  format="YYYY-MM-DD"
                  formatted="DD/MM/YYYY"
                  :placeholder="$t('message.chooseDate')"
                  :only-date="true"
                  :no-label="true"
                  required
                >
                </vue-ctk-date-time-picker>
              </fg-input>
              <fg-input :label="$t('message.dateDue')">
                <vue-ctk-date-time-picker
                  v-model="invoice.dueDate"
                  format="YYYY-MM-DD"
                  formatted="DD/MM/YYYY"
                  :placeholder="$t('message.chooseDate')"
                  :only-date="true"
                  :no-label="true"
                  required
                >
                </vue-ctk-date-time-picker>
              </fg-input>
            </b-form>
            <div slot="actions">
              <div class="d-none d-xl-block">
                <n-button
                  type="primary"
                  round
                  native-type="submit"
                  @click.native.prevent="submit"
                  :disabled="invalid || !!loader"
                  >{{ $t("message.submit") }}</n-button
                >
                <n-button
                  round
                  simple
                  @click.native.prevent="goBack('/invoices')"
                  >{{ $t("message.cancel") }}</n-button
                >
              </div>
            </div>
          </document-settings>
        </div>
        <!--  INVOICE SECTION -->
        <div class="col-12 col-xl-9">
          <invoice-form
            :invoice="invoice"
            @change="onInvoiceChange"
          ></invoice-form>
        </div>
      </div>
      <!--  ADDITIONAL SAVE BUTTON FOR MOBILE -->
      <div class="row">
        <div class="col d-block d-xl-none">
          <card class="d-flex align-items-center">
            <n-button
              type="primary"
              round
              native-type="submit"
              @click.native.prevent="submit"
              :disabled="invalid || !!loader"
              >{{ $t("message.submit") }}</n-button
            >
            <n-button
              round
              simple
              @click.native.prevent="goBack('/invoices')"
              >{{ $t("message.cancel") }}</n-button
            >
          </card>
        </div>
      </div>
    </ValidationObserver>
  </div>
</template>
<script>
import { mapActions, mapGetters } from "vuex";
import moment from "moment";
import _ from "lodash";
import { formatMoney, dateToHuman } from "@/helpers/helpers.js";
import { DEFAULT_INVOICE } from "@/store/accounting/invoices";
import InvoiceForm from "./InvoiceForm";
import { goBack } from "@/mixins";
import { DocumentSettings } from "@/components";

export default {
  mixins: [goBack],
  components: {
    InvoiceForm,
    DocumentSettings
  },
  data() {
    const dateNow = moment().format("YYYY-MM-DD");

    return {
      loader: null,
      invoice: {
        ..._.cloneDeep(DEFAULT_INVOICE),
        sentDate: dateNow,
        dueDate: dateNow
      }
    };
  },
  methods: {
    formatMoney,
    ...mapActions([
      "fetchUserSettings",
      "fetchCustomers",
      "addInvoice",
      "startPollingInvoiceNumberHistory",
      "stopPollingInvoiceNumberHistory"
    ]),
    setInvoiceDaysToPay(daysToPay) {
      if (!daysToPay) return;

      this.invoice.dueDate = moment(
        Date.now() + daysToPay * 86400000 // days to seconds
      ).format("YYYY-MM-DD");
    },
    onInvoiceChange(invoice) {
      this.invoice = invoice;
    },
    async submit() {
      this.loader = this.$loading.show();

      try {
        await this.addInvoice(this.invoice);
        // this.$router.push(`/invoices/view/${response.data.id}`);
        this.$router.push("/invoices");
        this.$notify({
          type: "success",
          title: this.$t("message.success"),
          text: this.$t("message.submitInvoiceSuccess")
        });
      } catch (error) {
        this.$notify({
          type: "danger",
          title: this.$t("message.error"),
          text: this.$t("message.submitInvoiceFailure") + ` ${error.message}.`
        });
      } finally {
        this.loader.hide();
      }
    },
    selectCustomerFromRouteQuery() {
      const { customer_id } = this.$route.query;

      if (!customer_id) return;

      const customer = _.find(this.customers, ["_id", customer_id]);

      if (customer) this.invoice.to = customer;
    }
  },
  computed: {
    ...mapGetters(["userSettings", "customers", "nextInvoiceNumber"]),
    customersOptions() {
      return [
        {
          value: {
            companyName: null,
            emailAddress: null,
            vatNumber: null,
            address: {
              line1: null,
              line2: null,
              district: null,
              region: null,
              country: null,
              postcode: null
            }
          },
          text: "Select a Customer",
          disabled: true
        },
        ...this.customers.map(customer => ({
          value: customer,
          text: customer.companyName
        }))
      ];
    }
  },
  filters: {
    dateToHuman
  },
  created() {
    this.fetchUserSettings();
    this.fetchCustomers().then(this.selectCustomerFromRouteQuery);
    this.startPollingInvoiceNumberHistory();

    this.invoice = this.$localStorage.get("invoice") || this.invoice;
  },
  destroyed() {
    this.stopPollingInvoiceNumberHistory();
    this.$localStorage.remove("invoice");
  },
  watch: {
    nextInvoiceNumber: {
      immediate: true,
      handler(value) {
        this.invoice.number = value;
      }
    },
    userSettings: {
      immediate: true,
      handler(value) {
        this.invoice.from = { ...value.company };
        this.invoice.items = _.map(this.invoice.items, item => ({
          ...item,
          vat: item.vat || value.invoicing.defaultVatRate
        }));
        this.setInvoiceDaysToPay(value.invoicing.defaultDaysToPay);
      }
    },
    invoice: {
      deep: true,
      handler(invoice) {
        this.$localStorage.set("invoice", invoice);
      }
    }
  }
};
</script>
<style lang="scss" scoped></style>
